"use client";

import { useEffect, useRef, useState } from "react";
import { GripHorizontal } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const highlights = [{
    index: "01",
    title: "Surgical Devices",
    description: "Precision-engineered surgical instruments and systems sourced from global leaders to ensure optimal clinical outcomes.",
    meta: "GLOBAL / SURGICAL / PRECISION",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_devices.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_devices.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_devices.webp-wvc-sizes",
    alt: "Advanced surgical instruments on a sterile tray"
  }, {
    index: "02",
    title: "Diagnostic Devices",
    description: "Advanced diagnostic imaging and testing equipment designed for early detection and accurate patient assessment.",
    meta: "CLINICAL / DIAGNOSTIC / ACCURATE",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/diagnostic_devices.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/diagnostic_devices.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/diagnostic_devices.png-wvc-sizes",
    alt: "Modern diagnostic imaging monitor in a hospital setting"
  }, {
    index: "03",
    title: "Medical Aesthetics",
    description: "Innovative dermatological and aesthetic solutions for non-invasive skin rejuvenation and professional care.",
    meta: "AESTHETIC / SKINCARE / INNOVATIVE",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/medical_aesthetics.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/medical_aesthetics.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/medical_aesthetics.png-wvc-sizes",
    alt: "Professional medical aesthetic equipment for skin treatment"
  }, {
    index: "04",
    title: "Dental Solutions",
    description: "Comprehensive dental technology and restorative materials to support modern dental practices worldwide.",
    meta: "DENTAL / RESTORATIVE / MODERN",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/dental_solutions.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/dental_solutions.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/dental_solutions.png-wvc-sizes",
    alt: "High-tech dental chair and equipment in a clinic"
  }, {
    index: "05",
    title: "AI Healthcare",
    description: "Next-generation AI-enabled diagnostic and workflow tools that enhance clinical decision-making and efficiency.",
    meta: "AI / DIGITAL / INTELLIGENT",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ai_healthcare.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ai_healthcare.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ai_healthcare.png-wvc-sizes",
    alt: "AI-powered medical data visualization on a screen"
  }, {
    index: "06",
    title: "Digital Healthcare",
    description: "Connected health platforms and digital infrastructure to streamline patient management and remote monitoring.",
    meta: "DIGITAL / CONNECTED / EFFICIENT",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/digital_healthcare.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/digital_healthcare.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/digital_healthcare.png-wvc-sizes",
    alt: "Digital health monitoring dashboard on a tablet"
  }];
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) {
        setProgress(0);
        return;
      }
      setProgress(Math.min(100, Math.max(0, el.scrollLeft / max * 100)));
    };
    handleScroll();
    el.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  return <section data-section-id="1289" id="productcategoryfilters" className="bg-background text-foreground relative overflow-hidden py-28 md:py-40">
      <div aria-hidden className="pointer-events-none absolute left-0 right-0 top-0 h-px origin-left bg-primary" style={{
      transform: "rotate(-6deg) translateY(-12px)"
    }} />
      <div aria-hidden className="pointer-events-none absolute right-6 top-0 size-1.5 bg-primary" style={{
      transform: "translateY(-14px)"
    }} />

      <span aria-hidden className="font-default pointer-events-none absolute -left-4 top-24 select-none uppercase leading-none text-foreground/[0.04]" style={{
      fontSize: "clamp(18rem, 32vw, 36rem)"
    }}>
        02
      </span>

      <div ref={headerRef} className={`relative mx-auto max-w-[1440px] px-6 md:px-12 transition-all duration-[520ms] ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-4">
            <div className="mb-6 flex items-center gap-3">
              <span className="font-default text-[0.75rem] font-medium uppercase tracking-[0.18em] text-primary">
                &sect; 02
              </span>
              <span className="font-default text-[0.75rem] font-medium uppercase tracking-[0.18em] text-foreground/70">
                / Categories
              </span>
              <span aria-hidden className="ml-1 h-px w-10 bg-border" />
            </div>
            <h2 className="font-default uppercase text-foreground" style={{
            fontSize: "clamp(3rem, 8vw, 7.5rem)",
            lineHeight: "0.86",
            letterSpacing: "-0.015em"
          }}>
              Global{" "}
              <span className="font-default italic text-primary" style={{
              letterSpacing: "0.01em"
            }}>
                Solutions
              </span>
            </h2>
          </div>

          <div className="md:col-span-8 md:pl-8 md:pt-6 lg:pl-16">
            <p className="font-default text-foreground/90" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            lineHeight: "1.55",
            letterSpacing: "-0.005em"
          }}>
              We bridge the gap between innovative medical manufacturers and healthcare professionals worldwide. Our curated portfolio ensures clinical excellence, regulatory compliance, and long-term value for our distribution partners.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <GripHorizontal className="size-5 text-primary" strokeWidth={1.5} />
              <span className="font-default text-[0.75rem] font-medium uppercase tracking-[0.18em] text-foreground/70">
                &larr; Drag &middot; 06 Categories
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-16 md:mt-24">
        <div ref={scrollRef} className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2 pl-6 pr-6 md:gap-8 md:pl-12 md:pr-0 cursor-grab active:cursor-grabbing" style={{
        scrollbarWidth: "none"
      }}>
          <style>{`#productcategoryfilters ::-webkit-scrollbar{display:none}`}</style>

          {highlights.map((item, i) => <article key={item.index} data-index={i} className="group relative shrink-0 snap-start bg-card border-[1.5px] border-border rounded-lg transition-[border-color,border-width] duration-[320ms] ease-out hover:border-[2.5px] hover:border-primary shadow-md" style={{
          width: "min(78vw, 420px)",
          height: "560px"
        }}>
              <span aria-hidden className="pointer-events-none absolute -left-px -top-px z-10 block border-l-[1.5px] border-t-[1.5px] border-primary transition-all duration-[320ms] ease-out" style={{
            width: "12px",
            height: "12px"
          }} />
              <span aria-hidden className="pointer-events-none absolute -left-px -top-px z-10 block opacity-0 border-l-[1.5px] border-t-[1.5px] border-primary transition-opacity duration-[320ms] ease-out group-hover:opacity-100" style={{
            width: "20px",
            height: "20px"
          }} />
              <span aria-hidden className="pointer-events-none absolute -bottom-px -right-px z-10 block border-b-[1.5px] border-r-[1.5px] border-primary transition-all duration-[320ms] ease-out" style={{
            width: "12px",
            height: "12px"
          }} />
              <span aria-hidden className="pointer-events-none absolute -bottom-px -right-px z-10 block opacity-0 border-b-[1.5px] border-r-[1.5px] border-primary transition-opacity duration-[320ms] ease-out group-hover:opacity-100" style={{
            width: "20px",
            height: "20px"
          }} />

              <div className="relative h-[60%] w-full overflow-hidden bg-muted rounded-t-lg">
                <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[320ms] ease-out group-hover:scale-[1.03]" />
                <span aria-hidden className="font-default pointer-events-none absolute left-4 top-4 text-[0.75rem] font-medium uppercase tracking-[0.18em] text-primary-foreground bg-primary px-2 py-1 rounded-md">
                  &sect; {item.index}
                </span>
              </div>

              <div className="flex h-[40%] flex-col justify-between p-6">
                <div>
                  <h3 className="font-default uppercase text-foreground" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
                lineHeight: "0.92",
                letterSpacing: "0em"
              }}>
                    {item.title}
                  </h3>
                  <p className="font-default mt-3 text-muted-foreground" style={{
                fontSize: "0.9375rem",
                lineHeight: "1.55"
              }}>
                    {item.description}
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <span aria-hidden className="h-px w-6 bg-primary" />
                  <span className="font-default text-[0.75rem] font-medium uppercase tracking-[0.14em] text-foreground/70">
                    {item.meta}
                  </span>
                </div>
              </div>
            </article>)}

          <div aria-hidden className="shrink-0" style={{
          width: "1px"
        }} />
        </div>

        <div className="mt-8 px-6 md:px-12">
          <div className="relative h-1.5 w-full bg-border rounded-full">
            <div className="absolute left-0 top-0 h-full bg-primary transition-[width] duration-150 ease-out rounded-full" style={{
            width: `${progress}%`
          }} />
            <div className="mt-3 flex items-center justify-between">
              <span className="font-default text-[0.7rem] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Scroll Progress
              </span>
              <span className="font-default text-[0.7rem] font-medium uppercase tracking-[0.18em] text-primary">
                {String(Math.round(progress)).padStart(3, "0")}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}